import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  CircularProgress,
  Divider,
  Paper,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
  CardActionArea,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import BlogCard from "./BlogCard";
import { BlogPost, getAllPosts, formatDate } from "../utils/blogUtils";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Default fallback image from Unsplash
const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1586281380117-5a60ae2050cc?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80";

const BlogPage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [hoveredFeatured, setHoveredFeatured] = useState(false);
  const [featuredImageError, setFeaturedImageError] = useState(false);

  useEffect(() => {
    async function fetchPosts() {
      try {
        const allPosts = await getAllPosts();
        setPosts(allPosts);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchPosts();
    // Scroll to the top when the component mounts
    window.scrollTo(0, 0);
  }, []);

  // Test validity of featured post image when posts change
  useEffect(() => {
    if (posts.length > 0 && posts[0].coverImage) {
      const img = new Image();
      img.src = posts[0].coverImage;
      img.onerror = () => setFeaturedImageError(true);
    }
  }, [posts]);

  const featuredPost = posts.length > 0 ? posts[0] : null;
  const remainingPosts = posts.slice(1);

  return (
    <>
      <Navbar />
      <Box
        sx={{
          pt: { xs: 4, md: 8 },
          pb: 10,
          minHeight: "100vh",
          backgroundColor: "#f9fafb",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                background: "linear-gradient(45deg, #2196F3, #21CBF3)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
              }}
            >
              Renoten Blog
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{
                maxWidth: 700,
                mx: "auto",
                fontSize: { xs: "1rem", md: "1.1rem" },
              }}
            >
              Insights, strategies, and tips to help you maximize productivity
              and achieve your goals
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 8 }}>
              <CircularProgress />
            </Box>
          ) : posts.length === 0 ? (
            <Typography align="center" sx={{ my: 8 }}>
              No blog posts found.
            </Typography>
          ) : (
            <Box>
              {/* Featured Post */}
              {featuredPost && (
                <Paper
                  elevation={2}
                  component={RouterLink}
                  to={`/blog/${featuredPost.slug}`}
                  onMouseEnter={() => setHoveredFeatured(true)}
                  onMouseLeave={() => setHoveredFeatured(false)}
                  sx={{
                    mb: 6,
                    borderRadius: 3,
                    overflow: "hidden",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: 8,
                    },
                    textDecoration: "none",
                    display: "block",
                    cursor: "pointer",
                  }}
                >
                  <Grid container>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{
                        height: { xs: 240, md: 480 },
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          backgroundImage: `url(${
                            featuredImageError
                              ? DEFAULT_IMAGE
                              : featuredPost.coverImage || DEFAULT_IMAGE
                          })`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          height: "100%",
                          width: "100%",
                          transition: "transform 0.3s ease",
                          transform: hoveredFeatured
                            ? "scale(1.05)"
                            : "scale(1)",
                        }}
                      />
                      <Chip
                        label="LATEST POST"
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 16,
                          left: 16,
                          backgroundColor: "primary.main",
                          color: "white",
                          fontWeight: 600,
                          zIndex: 2,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          p: { xs: 3, md: 5 },
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          height: "100%",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="overline"
                            color="text.secondary"
                            fontWeight={500}
                          >
                            {formatDate(featuredPost.date)}
                          </Typography>
                          <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                              fontWeight: 700,
                              mb: 2,
                              fontSize: { xs: "1.5rem", md: "2rem" },
                              color: hoveredFeatured
                                ? "primary.main"
                                : "inherit",
                              transition: "color 0.3s ease",
                            }}
                          >
                            {featuredPost.title}
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 3 }}>
                            {featuredPost.excerpt}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography
                            variant="button"
                            sx={{
                              color: "primary.main",
                              textTransform: "none",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              "& .arrow": {
                                transition: "transform 0.3s ease",
                                transform: hoveredFeatured
                                  ? "translateX(4px)"
                                  : "translateX(0)",
                                ml: 0.5,
                              },
                            }}
                          >
                            Read Full Article
                            <ArrowForwardIcon
                              className="arrow"
                              fontSize="small"
                            />
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Section Title */}
              <Typography
                variant="h5"
                component="h3"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  mt: 6,
                  px: 1,
                  borderLeft: "4px solid",
                  borderColor: "primary.main",
                }}
              >
                More Articles
              </Typography>

              {/* Remaining Posts in Grid */}
              <Grid container spacing={4}>
                {remainingPosts.map((post) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={post.slug}
                    sx={{ display: "flex" }}
                  >
                    <BlogCard post={post} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default BlogPage;
